/**
 * Subscriptions — the recurring third-party bills that, with AWS, make up what
 * serving a tenant costs us (V080).
 *
 * A subscription is either PLATFORM-WIDE (tenant_id null), whose cost the
 * margin view splits across brokerages pro-rata by their usage share, or pinned
 * to one brokerage and charged to it whole.
 *
 * Ending a subscription means setting `active_to`, not deleting it: the cost
 * was genuinely incurred in the months it ran, and removing the row would
 * rewrite margins already reported. Delete is offered for rows entered by
 * mistake and says so.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { adminService, billingService } from "@re-platform/api-client";
import type {
  BillingCycle,
  Subscription,
  TenantWithPlan,
} from "@re-platform/api-client";
import { Card, Toast } from "@re-platform/ui";

import { api } from "../../lib/api";
import { cardList, cardShell, Collapsible } from "../../lib/cards";

const CYCLES: { id: BillingCycle; label: string }[] = [
  { id: "MONTHLY", label: "Monthly" },
  { id: "ANNUAL", label: "Annual" },
  { id: "ONE_OFF", label: "One-off" },
];

const input: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  border: "1px solid var(--rp-color-border)",
  borderRadius: "var(--rp-radius-md)",
  background: "var(--rp-color-surface)",
  color: "var(--rp-color-text)",
  fontFamily: "inherit",
  fontSize: 13.5,
};

const label: React.CSSProperties = {
  display: "grid",
  gap: 5,
  fontSize: 12.5,
  fontWeight: 600,
  minWidth: 0,
};

function usd(v: string | number): string {
  const n = typeof v === "number" ? v : parseFloat(v);
  return isNaN(n) ? "—" : `$${n.toFixed(2)}`;
}

function cycleLabel(c: BillingCycle): string {
  return CYCLES.find((x) => x.id === c)?.label ?? c;
}

/** Blank form state — also what Add resets to. */
const EMPTY = {
  vendor: "",
  name: "",
  category: "",
  amount: "",
  billing_cycle: "MONTHLY" as BillingCycle,
  tenant_id: "",
  active_from: new Date().toISOString().slice(0, 10),
  notes: "",
};

export function Subscriptions() {
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<string | null>(null);
  const [includeEnded, setIncludeEnded] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const queryKey = useMemo(
    () => ["subscriptions", includeEnded] as const,
    [includeEnded],
  );

  const q = useQuery({
    queryKey,
    queryFn: () =>
      billingService.listSubscriptions(api, { include_ended: includeEnded }),
  });

  // For the "charge this to one brokerage" picker.
  const tenants = useQuery<TenantWithPlan[]>({
    queryKey: ["tenants"],
    queryFn: () => adminService.listTenants(api),
    staleTime: 60_000,
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
    // The margin view nets these off — a new subscription changes every bar.
    void queryClient.invalidateQueries({ queryKey: ["billing", "margin"] });
  }

  const create = useMutation({
    mutationFn: () =>
      billingService.createSubscription(api, {
        vendor: form.vendor.trim(),
        name: form.name.trim(),
        category: form.category.trim() || null,
        amount: form.amount,
        billing_cycle: form.billing_cycle,
        tenant_id: form.tenant_id || null,
        active_from: form.active_from,
        notes: form.notes.trim() || null,
      }),
    onSuccess: () => {
      setForm(EMPTY);
      invalidate();
      setToast("Subscription added.");
    },
    onError: (e: Error) => setToast(e.message),
  });

  const end = useMutation({
    mutationFn: (s: Subscription) =>
      billingService.updateSubscription(api, s.id, {
        active_to: new Date().toISOString().slice(0, 10),
      }),
    onSuccess: () => {
      invalidate();
      setToast("Ended today. Past periods keep the cost.");
    },
    onError: (e: Error) => setToast(e.message),
  });

  const remove = useMutation({
    mutationFn: (s: Subscription) =>
      billingService.deleteSubscription(api, s.id),
    onSuccess: () => {
      invalidate();
      setToast("Deleted.");
    },
    onError: (e: Error) => setToast(e.message),
  });

  const rows = q.data ?? [];
  const monthlyTotal = rows
    .filter((r) => !r.active_to)
    .reduce((acc, r) => acc + (parseFloat(r.monthly_equivalent) || 0), 0);

  const canSubmit =
    form.vendor.trim().length > 0 &&
    form.name.trim().length > 0 &&
    parseFloat(form.amount) >= 0 &&
    !create.isPending;

  return (
    <>
      <h1 style={{ marginTop: 0, marginBottom: 4 }}>Subscriptions</h1>
      <p
        style={{
          color: "var(--rp-color-text-muted)",
          marginTop: 0,
          marginBottom: 20,
        }}
      >
        The recurring bills we pay to run the platform. These are netted off
        revenue in the margin view — a platform-wide one is split across
        brokerages by their share of usage, a brokerage-specific one is charged
        to it whole.
      </p>

      <Card padding="lg" style={{ marginBottom: 16 }}>
        <Collapsible title="Add a subscription" defaultOpen={false}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) create.mutate();
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: 14,
              }}
            >
              <label style={label}>
                Vendor
                <input
                  style={input}
                  value={form.vendor}
                  placeholder="ElevenLabs"
                  onChange={(e) =>
                    setForm((f) => ({ ...f, vendor: e.target.value }))
                  }
                />
              </label>
              <label style={label}>
                Plan
                <input
                  style={input}
                  value={form.name}
                  placeholder="Voice — Starter"
                  onChange={(e) =>
                    setForm((f) => ({ ...f, name: e.target.value }))
                  }
                />
              </label>
              <label style={label}>
                Category
                <input
                  style={input}
                  value={form.category}
                  placeholder="voice"
                  onChange={(e) =>
                    setForm((f) => ({ ...f, category: e.target.value }))
                  }
                />
              </label>
              <label style={label}>
                Amount (USD)
                <input
                  style={input}
                  value={form.amount}
                  inputMode="decimal"
                  placeholder="22.00"
                  onChange={(e) =>
                    setForm((f) => ({ ...f, amount: e.target.value }))
                  }
                />
              </label>
              <label style={label}>
                Billing cycle
                <select
                  style={input}
                  value={form.billing_cycle}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      billing_cycle: e.target.value as BillingCycle,
                    }))
                  }
                >
                  {CYCLES.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </label>
              <label style={label}>
                Starts
                <input
                  type="date"
                  style={input}
                  value={form.active_from}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, active_from: e.target.value }))
                  }
                />
              </label>
              <label style={label}>
                Charged to
                <select
                  style={input}
                  value={form.tenant_id}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, tenant_id: e.target.value }))
                  }
                >
                  <option value="">All brokerages (split by usage)</option>
                  {(tenants.data ?? []).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.display_name}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ ...label, gridColumn: "1 / -1" }}>
                Notes
                <input
                  style={input}
                  value={form.notes}
                  placeholder="Optional — e.g. usage-based, amount is a running average"
                  onChange={(e) =>
                    setForm((f) => ({ ...f, notes: e.target.value }))
                  }
                />
              </label>
            </div>
            <div style={{ marginTop: 16 }}>
              <button
                type="submit"
                className="rp-btn rp-btn--primary"
                disabled={!canSubmit}
              >
                {create.isPending ? "Adding…" : "Add subscription"}
              </button>
            </div>
          </form>
        </Collapsible>
      </Card>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 12,
        }}
      >
        <span style={{ fontSize: 13.5 }}>
          <strong>{usd(monthlyTotal)}</strong>
          <span style={{ color: "var(--rp-color-text-muted)" }}>
            {" "}
            per month, active subscriptions
          </span>
        </span>
        <label
          style={{
            marginLeft: "auto",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 13,
            color: "var(--rp-color-text-muted)",
          }}
        >
          <input
            type="checkbox"
            checked={includeEnded}
            onChange={(e) => setIncludeEnded(e.target.checked)}
          />
          Show ended
        </label>
      </div>

      {q.isPending && (
        <p style={{ color: "var(--rp-color-text-muted)", fontSize: 13 }}>
          Loading…
        </p>
      )}
      {q.isError && (
        <p style={{ color: "var(--rp-color-danger)", fontSize: 13 }}>
          {q.error instanceof Error
            ? q.error.message
            : "Could not load subscriptions"}
        </p>
      )}
      {!q.isPending && !q.isError && rows.length === 0 && (
        <p style={{ color: "var(--rp-color-text-muted)", fontSize: 13 }}>
          Nothing yet — add the first one above.
        </p>
      )}

      <div style={cardList}>
        {rows.map((s) => {
          const ended = !!s.active_to;
          return (
            <div key={s.id} className="rp-clientcard" style={cardShell}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ fontWeight: 700, fontSize: 14.5 }}>
                  {s.vendor}
                  <span
                    style={{
                      fontWeight: 400,
                      color: "var(--rp-color-text-muted)",
                    }}
                  >
                    {" "}
                    · {s.name}
                  </span>
                </div>
                <div
                  style={{
                    color: "var(--rp-color-text-muted)",
                    fontSize: 12.5,
                    marginTop: 2,
                  }}
                >
                  {[
                    cycleLabel(s.billing_cycle),
                    s.category,
                    s.tenant_name ?? "All brokerages",
                    ended ? `ended ${s.active_to}` : `since ${s.active_from}`,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
                {s.notes && (
                  <div
                    style={{
                      color: "var(--rp-color-text-muted)",
                      fontSize: 12,
                      marginTop: 4,
                      fontStyle: "italic",
                    }}
                  >
                    {s.notes}
                  </div>
                )}
              </div>

              <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                <div style={{ fontWeight: 700, fontSize: 14.5 }}>
                  {usd(s.amount)}
                </div>
                {/* An annual figure says nothing about the monthly run rate,
                    which is what the total above is built from. */}
                {s.billing_cycle !== "MONTHLY" && (
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--rp-color-text-muted)",
                    }}
                  >
                    {s.billing_cycle === "ONE_OFF"
                      ? "one-off"
                      : `${usd(s.monthly_equivalent)}/mo`}
                  </div>
                )}
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  marginLeft: "auto",
                }}
              >
                {!ended && (
                  <button
                    type="button"
                    className="rp-btn rp-btn--secondary rp-btn--sm"
                    disabled={end.isPending}
                    title="Stop counting from today; past periods keep the cost"
                    onClick={() => end.mutate(s)}
                  >
                    End
                  </button>
                )}
                <button
                  type="button"
                  className="rp-btn rp-btn--danger rp-btn--sm"
                  disabled={remove.isPending}
                  title="Removes it from PAST margins too — use End unless this was entered by mistake"
                  onClick={() => {
                    if (
                      confirm(
                        `Delete ${s.vendor} · ${s.name}?\n\nThis removes its cost from periods it was actually billed in, changing margins already reported. Use "End" instead unless this row was a mistake.`,
                      )
                    ) {
                      remove.mutate(s);
                    }
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <Toast message={toast} onDismiss={() => setToast(null)} />
    </>
  );
}
